import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { checkElementInView } from '../../../js/__utils/checkElementInView';
import afterPaint from '../../../js/__utils/afterPaint';
import throttle from 'lodash/throttle';

const openEvent = new Event('navigation-opened');
const closeEvent = new Event('navigation-closed');

const scrollableElements = document.querySelectorAll(
	'.block-navigation__menu, .block-navigation.is-style-full-width-dropdown .block-navigation-submenu, .block-navigation-submenu'
);

const navigationBlocks = document.querySelectorAll('.block-navigation');

const navigationHamburgers = document.querySelectorAll(
	'.block-navigation__hamburger'
);

const menuPrimaryButton = document.querySelector(
	'.block-navigation__menu .is-style-primary'
);

const mainHeader = document.querySelector('.main-header');

const currentMainHeaderStyle = mainHeader.className;

const contentElement = document.querySelector('#content');

if (contentElement && mainHeader) {
	checkElementInView(
		contentElement,
		() => {
			mainHeader.className = currentMainHeaderStyle;
			mainHeader.classList.remove('scrolled');
		},
		() => {
			mainHeader.classList.remove('is-style-transparent');
			mainHeader.classList.add('is-style-white');
			mainHeader.classList.add('scrolled');
		},
		{
			threshold: [0],
			rootMargin: '0px 0px 0px 0px',
		}
	);
}

if (null !== menuPrimaryButton) {
	const menuPrimaryButtonClone = menuPrimaryButton.cloneNode(true);
	menuPrimaryButtonClone.classList.add(
		'block-navigation__contact-btn--mobile',
		'wp-block-button--small'
	);
	menuPrimaryButton.classList.add('block-navigation__contact-btn');

	document
		.querySelector('.block-navigation__container')
		.append(menuPrimaryButtonClone);
}

const toggleNavigationHamburger = (e) => {
	const currentNavigationHamburger = e?.currentTarget ?? e;
	currentNavigationHamburger.classList.toggle('active');

	if (currentNavigationHamburger.classList.contains('active')) {
		window.dispatchEvent(openEvent);
		setFirstLevelButtonBlockOffsets();
		setNavMenuActiveClass(currentNavigationHamburger, true);
		currentNavigationHamburger.setAttribute('aria-expanded', true);

		if (window.innerWidth < 992) {
			lockScroll();
		}
	} else {
		window.dispatchEvent(closeEvent);

		clearAllBodyScrollLocks();

		setNavMenuActiveClass(currentNavigationHamburger, false);
		currentNavigationHamburger.setAttribute('aria-expanded', false);
	}
};

const submenuClosed = () => {
	if (window.innerWidth >= 992) {
		clearAllBodyScrollLocks();
	}
};

const setFirstLevelButtonBlockOffsets = () => {
	if (0 === navigationBlocks.length) {
		return;
	}

	// Measure every button before writing, so this costs one reflow, not one per button.
	const measurements = [];

	navigationBlocks.forEach((navigationBlock) => {
		const firstLevelButtonBlocks = navigationBlock.querySelectorAll(
			'.block-navigation__menu > .wp-block-button'
		);

		if (0 === firstLevelButtonBlocks.length) {
			return;
		}

		const heights = [];

		firstLevelButtonBlocks.forEach((firstLevelButtonBlock) => {
			heights.push(firstLevelButtonBlock.offsetHeight);
		});

		measurements.push({
			navigationBlock,
			firstLevelButtonBlocks,
			heights,
		});
	});

	measurements.forEach(
		({ navigationBlock, firstLevelButtonBlocks, heights }) => {
			let totalOffset = 0;

			for (let i = firstLevelButtonBlocks.length - 1; i >= 0; i--) {
				firstLevelButtonBlocks[i].style.setProperty(
					'--buttonOffset',
					`${totalOffset / 16}rem`
				);

				totalOffset += heights[i];
			}

			navigationBlock.style.setProperty(
				'--totalButtonOffset',
				`${totalOffset / 16}rem`
			);
		}
	);
};

const setNavMenuActiveClass = (element, navActive = null) => {
	const currentElement = element?.detail?.currentElement ?? element;

	if (!currentElement) {
		return;
	}

	const currentNavigationBlock = currentElement.closest('.block-navigation');

	if (!currentNavigationBlock) {
		return;
	}

	if (null === navActive) {
		if (
			currentElement.classList.contains('active') ||
			currentElement.parentElement.classList.contains('active') ||
			currentNavigationBlock.querySelector(
				'.block-navigation__hamburger.active'
			)
		) {
			navActive = true;
		} else {
			navActive = false;
		}
	}

	if (true === navActive) {
		currentNavigationBlock.classList.add('active');
		document.addEventListener('keydown', clickOffMenu);
		document.addEventListener('click', clickOffMenu);
	} else {
		currentNavigationBlock.classList.remove('active');
		document.removeEventListener('keydown', clickOffMenu);
		document.removeEventListener('click', clickOffMenu);
	}

	navigationBlocks.forEach((navigationBlock) => {
		if (navigationBlock !== currentNavigationBlock) {
			navigationBlock.classList.remove('active');
		}
	});
};

const lockScroll = () => {
	if (scrollableElements.length) {
		scrollableElements.forEach(function (scrollableElement) {
			const storedRequestAnimationFrame = window.requestAnimationFrame;
			window.requestAnimationFrame = () => 42;

			disableBodyScroll(scrollableElement, {
				reserveScrollBarGap: true,
			});
			window.requestAnimationFrame = storedRequestAnimationFrame;
		});
	}
};

const clickOffMenu = (e) => {
	if (0 === navigationHamburgers.length) {
		return;
	}

	let closestMenu;
	let closestHamburger;

	if (e.key === undefined && e.target) {
		closestMenu = e.target.closest('.block-navigation');
		closestHamburger = e.target.closest('.block-navigation__hamburger');
	}

	if (e.key && e.key !== 'Escape') {
		return;
	}

	if (
		closestMenu?.contains(e.target) ||
		closestHamburger?.contains(e.target)
	) {
		return;
	}

	navigationHamburgers.forEach((navigationHamburger) => {
		if (navigationHamburger.classList.contains('active')) {
			toggleNavigationHamburger(navigationHamburger);
		}
	});

	window.dispatchEvent(closeEvent);
};

if (navigationHamburgers.length > 0) {
	navigationHamburgers.forEach((navigationHamburger) => {
		navigationHamburger.addEventListener(
			'click',
			toggleNavigationHamburger
		);
	});
}

// Opening the mobile menu recalculates these, so keep the initial pass off the critical path.
afterPaint(setFirstLevelButtonBlockOffsets);

if (navigationBlocks.length > 0) {
	navigationBlocks.forEach((navigationBlock) => {
		navigationBlock.classList.add('block-navigation--initialized');
	});
}

window.addEventListener('navigation-submenu-closed', submenuClosed);
window.addEventListener('navigation-submenu-closed', setNavMenuActiveClass);
window.addEventListener('navigation-submenu-opened', setNavMenuActiveClass);
window.addEventListener(
	'resize',
	throttle(setFirstLevelButtonBlockOffsets, 100)
);
